import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, InputAdornment } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import * as ProfilesService from 'services/api/ProfilesService';
import { UserContext } from 'components/UserGuard';
import Modal from 'components/shared/Modal';
import { useWindowSize } from 'util/Window';
import styles from 'components/profile/EditProfilePageView/EditProfileForm/EditProfileForm.module.scss';
import { VerifyPhoneButton } from 'components/shared/VerifyPhoneButton';
import { validatePhoneE164 } from 'utils/phoneFormatter';
import { VerifyEmailButton } from 'components/shared/VerifyEmailButton';

export const HomeVerifyContactInfoModal: React.FunctionComponent = () => {
  const { isMobile } = useWindowSize();
  const user = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user.email)
  const [phone, setPhone] = useState(user.phone)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)
  const [verified, setVerified] = useState< any | undefined>(undefined);
  
  useEffect(() => {
    async function load() {
      setLoading(true)
      if(user.nccerCardNumber){
        const res = await ProfilesService.GetUserVerified(user.nccerCardNumber);
        setVerified(res);
        setLoading(false)
      }
      else{
        setVerified("didn't work")
        setLoading(false)
      }
    }
    load().catch((error) => console.error(error));
  }, [user]);

  const PhoneVerified = useCallback(() => {
    user.refetchUser();
  }, []);


  const phoneCodeSubmittable = useMemo(() => phone && phone?.replace("+", "").length > 10 ,[phone])
  
    const verifiedPhone = useMemo(() => verified && Array.isArray(verified)? verified.find((items: { name: string; value: string }) => items.name === "phone_number") : null,[verified, user])
    const verifiedPhoneVerified = useMemo(() => verified && Array.isArray(verified)? verified.find((items: { name: string; value: string }) => items.name === "phone_number_verified") : null,[verified, user])
    const [display, setDisplay] = React.useState<boolean | null>(null)
    useEffect(() => {
      if(!phone || (phone && !/\d/.test(phone))){
        //if there is no phone no phone number
        setDisplay(null)
        setPhoneVerified(false)
      } else if (verifiedPhoneVerified?.value == "true" && phone?.replace("+", "") == verifiedPhone?.value?.replace("+", "")){
        //We do not store the + because aws. If the current number is verified, mark verified
        setDisplay(true)
        setPhoneVerified(true)
      } else {
        //if the current phone is there and not verified, or changed
        setDisplay(false)
        setPhoneVerified(false)
      }
    }, [verified, phone, verifiedPhoneVerified, verifiedPhone, user])

  const emailCodeSubmittable = useMemo(() => email && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email) ,[email])
    const verifiedEmail = useMemo(() => verified && Array.isArray(verified)? verified.find((items: { name: string; value: string }) => items.name === "email") : null,[verified, user])
    const verifiedEmailVerified = useMemo(() => verified && Array.isArray(verified)? verified.find((items: { name: string; value: string }) => items.name === "email_verified") : null,[verified, user])
    const [displayEmail, setDisplayEmail] = React.useState<boolean | null>(null)
    useEffect(() => {
      if(!email){
        setDisplayEmail(null)
        setEmailVerified(false)
      } else if (verifiedEmailVerified?.value == "true" && email == verifiedEmail?.value?.toLowerCase()){
        setDisplayEmail(true)
        setEmailVerified(true)
      } else {
        setDisplayEmail(false)
        setEmailVerified(false)
      }
    }, [verified, email, verifiedEmailVerified, verifiedEmail, user])

  const DisplayVerified = useMemo(() => {
    if(display == null){
      return <Typography color='red' display={'flex'} alignItems={'center'} justifyContent={'center'}>No Phone Number</Typography>
    }
    else if (display){
      return <Typography color='green' display={'flex'} alignItems={'center'} justifyContent={'center'}> <CheckCircleOutline />Verified</Typography>
    }
    return <Typography color='orange' display={'flex'} alignItems={'center'} justifyContent={'center'}>Unverified</Typography>
  }, [display])

  const DisplayEmailVerified = useMemo(() => {
    if(displayEmail == null){
      return <Typography color='red' display={'flex'} alignItems={'center'} justifyContent={'center'}>No Email</Typography>
    }
    else if (displayEmail){
      return <Typography color='green' display={'flex'} alignItems={'center'} justifyContent={'center'}> <CheckCircleOutline />Verified</Typography>
    }
    return <Typography color='orange' display={'flex'} alignItems={'center'} justifyContent={'center'}>Unverified</Typography>
  }, [displayEmail])

  const closeModal = useCallback(() => {
    setOpen(false)
  }, []);

  useEffect(() => {
    if(verified != undefined && (verifiedPhoneVerified?.value == undefined && verifiedEmailVerified?.value == undefined)){
      setOpen(true);
    } else if ((verifiedPhoneVerified?.value == "true" || verifiedEmailVerified?.value == "true")) {
      setOpen(false)
    }
  }, [verified, verifiedPhoneVerified, verifiedEmailVerified])

  return (
    <HomeVerifyContactInfoModalContext.Provider value={{ setLoading, closeModal }}>
      <Modal
        loading={loading}
        open={open}
        title={"Verify your contact info"}
        subTitleText={'Verify your account contact information and help us keep it up to date.'}
        onClose={closeModal}
        actions={
          <Box sx={{display:"flex", flexDirection: 'row', justifyContent: 'right', gap:2, width:1, pt:4}}>
            <Button color="secondary" variant="outlined" onClick={closeModal}>
              Not now
            </Button>
          </Box>
        }
        >
        <Box sx={{ display: 'flex', flexDirection: 'column',  marginTop: '10px', paddingBottom: '15px' }}>
          Email
          <Box sx={{ display: 'flex', flexDirection: isMobile? 'column' : 'row', marginTop: '10px', gap: 3 }}>
          <TextField
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required={true}
            disabled={false}
            className={styles.inputFieldLeft}
            sx={{ width: '100%', paddingRight: isMobile? "0px!important" : 'auto', justifyContent: "end",}}
            InputProps={{ endAdornment: <InputAdornment position="end">{DisplayEmailVerified}</InputAdornment>}}
            // Slot props will be used in a future mui update. Input props will look like this instead
            // slotProps={{
            //   input: {
            //     endAdornment: <InputAdornment position="end">{DisplayVerified}</InputAdornment>,
            //   },
            // }}
          />
          {!emailVerified && <VerifyEmailButton disabled={!emailCodeSubmittable } email={email? email : ''} exitEditing={PhoneVerified}/>}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column',  paddingTop: '15px' }}>
          Phone
          <Box sx={{ display: 'flex', flexDirection: isMobile? 'column' : 'row', marginTop: '10px', gap: 3 }}>
          <TextField
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            required={true}
            disabled={false}
            className={styles.inputFieldLeft}
            sx={{ width: '100%', paddingRight: isMobile? "0px!important" : 'auto', justifyContent: "end",}}
            InputProps={{ endAdornment: <InputAdornment position="end">{DisplayVerified}</InputAdornment>}}
            // Slot props will be used in a future mui update. Input props will look like this instead
            // slotProps={{
            //   input: {
            //     endAdornment: <InputAdornment position="end">{DisplayVerified}</InputAdornment>,
            //   },
            // }}
          />
          {!phoneVerified && <VerifyPhoneButton disabled={!validatePhoneE164(phone) || !phoneCodeSubmittable } phone={phone? phone : ''} exitEditing={PhoneVerified}/>}
          </Box>
          {validatePhoneE164(phone) != true && <Typography color='error' variant='subtitle2'>{validatePhoneE164(phone)}</Typography>}
        </Box>
      </Modal>
    </HomeVerifyContactInfoModalContext.Provider>
  );
}

interface HomeVerifyContactInfoModalContext {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

const HomeVerifyContactInfoModalContext = createContext<HomeVerifyContactInfoModalContext>({
  setLoading: () => undefined,
  closeModal: () => undefined,
});